import React, {useEffect} from 'react';
import {useDispatch} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import {ProfileTableHeader} from 'src/components/profile-tab-structure';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {NoResultPage} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';

import {BoxLoading} from '@pb/kit-component-box-loading';

import {GeneratedExcelPluginUsageReportsTable} from './generated-excel-plugin-usage-reports-table';
import {excelPluginUsageReportsStateActions} from './slices/excel-plugin-usage-reports-state.slice';
import {excelPluginUsageReportsActions} from './slices/excel-plugin-usage-reports.slice';
import {useGetExcelPluginUsageReports} from './useGetExcelPluginUsageReports';

import type {GeneratedReport} from '../typedef';

type Props = {
    accountId: number,
    onOpenReport: (report: GeneratedReport) => void,
};

export const GeneratedExcelPluginUsageReports = ({accountId, onOpenReport}: Props) => {
    const dispatch = useDispatch();

    const {
        result,
        loadingError,
        pagination,
    } = useGetExcelPluginUsageReports({accountId});

    useEffect(() => {
        return () => {
            dispatch(excelPluginUsageReportsActions.clearData());
            dispatch(excelPluginUsageReportsStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <ServerResponseHandler serverError={loadingError}>
            <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                <BoxLoading loading={!result}>
                    <ProfileTableHeader caption="Generated Reports" entityCaption="Report" totalCount={result?.totalCount}/>
                    {result?.data.length ? (
                        <div style={{minHeight: 66}}>
                            <GeneratedExcelPluginUsageReportsTable
                                rows={result.data}
                                totalCount={result?.totalCount || 0}
                                pagination={pagination}
                                onOpenReportDrawer={onOpenReport}
                            />
                        </div>
                    ) : (
                        <NoResultPage
                            title="No reports for the last 3 days to show"
                            description=""
                        />
                    )}
                </BoxLoading>
            </ErrorBoundary>
        </ServerResponseHandler>
    );
};
